/**
 * TurnstileField.jsx — Cloudflare Turnstile widget wrapper.
 *
 * Phase 12.1a · Sprint 12.1.
 *
 * One reusable component that:
 *   1. Reads the public sitekey from `REACT_APP_TURNSTILE_SITE_KEY`.
 *   2. Renders the Turnstile widget once the form is in view.
 *   3. Surfaces the token via the `onToken` callback so the parent
 *      form can include it in its submit payload (field name
 *      `turnstile_token` to match the backend `BaseModel`s).
 *   4. Clears the token on expire/error so a stale token never gets
 *      shipped to the backend.
 *
 * Fail-open: if `REACT_APP_TURNSTILE_SITE_KEY` is unset (preview
 * environments, local dev before keys are provisioned), the
 * component renders `null` — the form still works because the
 * backend is in fail-open mode too.
 *
 * Usage:
 *   const [tsToken, setTsToken] = useState("");
 *   <TurnstileField action="signup" onToken={setTsToken} />
 *   // include `turnstile_token: tsToken` in your POST body.
 */
import React from "react";
import Turnstile from "react-turnstile";


const SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();


export default function TurnstileField({
  action,                  // semantic tag — must match backend `expected_action`
  onToken,                 // (token: string) => void
  onError,                 // (err: any) => void — optional
  theme = "dark",          // "auto" | "light" | "dark"
  size  = "normal",        // "normal" | "compact"
}) {
  if (!SITE_KEY) {
    // Fail-open render — backend skips verification too.
    return null;
  }

  return (
    <div data-testid="turnstile-field" style={{ marginTop: 10 }}>
      <Turnstile
        sitekey={SITE_KEY}
        action={action}
        theme={theme}
        size={size}
        retry="auto"
        refreshExpired="auto"
        onVerify={(token) => onToken?.(token || "")}
        onExpire={() => onToken?.("")}
        onError={(err) => {
          onToken?.("");
          onError?.(err);
        }}
      />
    </div>
  );
}


/**
 * Tiny helper: returns `true` when Turnstile is configured for this
 * environment. Forms can use it to decide whether to render an
 * extra "I am a human" hint string. Most callers can ignore this.
 */
export function turnstileEnabled() {
  return Boolean(SITE_KEY);
}
